import { selectDefaultModeFor } from "@/modes";
import {
  FeaturesState,
  selectFeaturesSlice,
} from "@/store/features/features-slice";
import { selectIElementsSlice } from "@/store/ielements-slice-selector";
import { selectShouldUseIntensityData } from "@/store/modes/walk-mode-slice";
import { curryAppSelector } from "@/store/reselect";
import { RootState } from "@/store/store";
import {
  selectCanReadPointCloud,
  selectSubscriptionFeaturesSlice,
} from "@/store/subscription-features/subscription-features-selectors";
import { SubscriptionFeaturesState } from "@/store/subscription-features/subscription-features-slice";
import { GUID } from "@faro-lotv/foundation";
import {
  IElementTypeHint,
  isIElementSectionDataSession,
} from "@faro-lotv/ielement-types";
import {
  IElementsState,
  selectIElement,
  selectIElementChildren,
  selectIsDataSessionEmpty,
} from "@faro-lotv/project-source";
import { createSelector } from "@reduxjs/toolkit";
import { TreeNodeDisabledReason } from "../tree-types";

/**
 * A memoized selector to get the reason why a tree node is disabled.
 *
 * @param state The state of the app store
 * @param id The ID of the tree node
 * @returns The memoized selector
 */
export const selectTreeNodeDisabledReason = curryAppSelector(
  createSelector(
    [
      selectIElementsSlice,
      selectSubscriptionFeaturesSlice,
      selectFeaturesSlice,
      selectShouldUseIntensityData,
      (state: RootState, id: GUID) => id,
    ],
    computeTreeNodeDisabledReason,
  ),
);

/**
 * @returns a reason why a node is disabled or undefined otherwise
 * @param iElements the iElements slice of the store
 * @param subscriptionFeatures the subscriptions slice of the store
 * @param features the features slice of the store
 * @param shouldUseIntensityData whether to use intensity data
 * @param id the iElement id of the node
 */
function computeTreeNodeDisabledReason(
  iElements: IElementsState,
  subscriptionFeatures: SubscriptionFeaturesState,
  features: FeaturesState,
  shouldUseIntensityData: boolean,
  id: GUID,
): TreeNodeDisabledReason | undefined {
  const state = { iElements };
  const element = selectIElement(id)(state);
  if (!element) return;

  if (isIElementSectionDataSession(element)) {
    const children = selectIElementChildren(element.id)(state);
    if (children.length !== element.childrenIds?.length) {
      // Returning here if the data session has children that are not loaded yet
      return;
    }
    const isPointCloudOnly = children.every(
      (el) => el.typeHint === IElementTypeHint.dataSetPCloudUpload,
    );

    // Check if the data session only contains a point cloud with no 2d data
    // and the company does not have the point cloud bundle
    // So that we can display a specific message
    const isMissingPCPermission =
      isPointCloudOnly && !selectCanReadPointCloud({ subscriptionFeatures });

    if (isMissingPCPermission) {
      return TreeNodeDisabledReason.missingPointCloudPermission;
    }

    const isDataSessionEmpty = selectIsDataSessionEmpty(element)(state);

    // Disable data session if it's empty (no path, nor stream, nor floorplan)
    if (isDataSessionEmpty) {
      return TreeNodeDisabledReason.notSupported;
    }
  }

  // Check if the viewer can display the element
  if (
    selectDefaultModeFor(
      {
        iElements,
        subscriptionFeatures,
        features,
      },
      element,
      shouldUseIntensityData,
    ) === undefined
  ) {
    return TreeNodeDisabledReason.notSupported;
  }
}
